import { useState, useEffect } from 'react';

const navItems = [
  { label: 'Escritório', id: 'sobre' },
  { label: 'Áreas de Atuação', id: 'areas' },
  { label: 'Como Atuamos', id: 'como-atuamos' },
  { label: 'Contato', id: 'contato' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <>
      {/* ── Overlay (mobile only) ── */}
      <div
        className={`fixed inset-0 z-[55] bg-black/50 backdrop-blur-sm transition-all duration-300 md:hidden ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      {/* ── Right Sidebar Drawer (mobile only) ── */}
      <div
        className={`fixed top-0 right-0 h-full w-72 bg-[#1F2A44] z-[60] flex flex-col shadow-2xl transition-transform duration-300 ease-in-out md:hidden ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navegação"
      >
        {/* Drawer header — logo centered */}
        <div className="flex items-center justify-center px-6 border-b border-white/10 h-[100px] flex-shrink-0">
          <div className="flex items-center h-[72px]">
            <img
              src="https://static.readdy.ai/image/2751d92eb47f633f00285d5acf7c03f9/ef045d39655e3e00acde9d0b7a8eacd3.png"
              alt="Jocimar Tadioto Advocacia"
              className="h-full w-auto object-contain"
            />
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex flex-col px-6 pt-6 gap-1 flex-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-white/75 hover:text-[#C8B07A] hover:bg-white/5 transition-all duration-200 text-left text-sm font-medium py-3 px-3 rounded-md cursor-pointer whitespace-nowrap border-b border-white/5 last:border-0"
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* WhatsApp CTA at bottom */}
        <div className="px-6 pb-10 flex-shrink-0">
          <a
            href="https://wa.me/5567999210516"
            target="_blank"
            rel="nofollow noreferrer"
            className="flex items-center justify-center gap-2 bg-[#C8B07A] hover:bg-[#D6C18F] text-[#1F2A44] px-4 py-3 rounded-md font-bold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-lg"></i>
            </span>
            Falar pelo WhatsApp
          </a>
        </div>
      </div>

      {/* ── Top Header Bar ── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#1F2A44] md:bg-transparent ${
          scrolled
            ? 'md:bg-[#1F2A44]/90 md:backdrop-blur-md'
            : 'md:bg-transparent md:backdrop-blur-none'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-10 relative flex items-center h-[110px] md:justify-between">
          {/* Logo — centered on mobile, left on desktop */}
          <div className="absolute left-1/2 -translate-x-1/2 md:static md:translate-x-0 flex items-center h-[92px]">
            <img
              src="https://static.readdy.ai/image/2751d92eb47f633f00285d5acf7c03f9/ef045d39655e3e00acde9d0b7a8eacd3.png"
              alt="Jocimar Tadioto Advocacia"
              className="h-full w-auto object-contain"
            />
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`transition-colors duration-300 text-sm font-medium tracking-wide cursor-pointer whitespace-nowrap ${
                  scrolled
                    ? 'text-white/80 hover:text-[#C8B07A]'
                    : 'text-white/90 hover:text-[#C8B07A] font-semibold'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Desktop WhatsApp CTA */}
          <a
            href="https://wa.me/5567999210516"
            target="_blank"
            rel="nofollow noreferrer"
            className="hidden md:inline-flex items-center gap-2 bg-[#C8B07A] hover:bg-[#D6C18F] text-[#1F2A44] px-5 py-2.5 rounded-md font-bold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-whatsapp-line text-lg"></i>
            </span>
            Falar pelo WhatsApp
          </a>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden ml-auto flex items-center justify-center w-8 h-8 cursor-pointer relative z-10 text-white"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir menu"
          >
            <span className="w-8 h-8 flex items-center justify-center">
              <i className="ri-menu-line text-2xl"></i>
            </span>
          </button>
        </div>
      </header>
    </>
  );
}
